import {
  View,
  StyleSheet,
  ActivityIndicator,
  Text,
  FlatList,
} from "react-native";
import React, { useCallback, useEffect, useState, memo } from "react";
import { TouchableNativeFeedback } from "react-native-gesture-handler";

import { colors } from "@constants/theme";
import analytics from "@services/analytics";
import { MY_DELIVERY_EVENTS } from "@constants/analyticsEventsCT";
import { getUpcomingDeliveries } from "@services/api";
import { Cash, Gullak, Order, ReturnOrder } from "@assets/svg/MyDeliveries";
import RightArrow from "@assets/svg/RightArrow";
import t from "@constants/localization";
import { getDayMonth } from "@utils/getFormattedDate";
import { scale } from "@utils/scale";
import OnLeave from "@assets/svg/OnLeave";
import Updating from "@assets/svg/Updating";
import RenderHtml from "react-native-render-html";
import { useSelector } from "react-redux";
import ListEmptyComponent from "./ListEmptyComponent";
import { getReturnAmount } from "../utils";

const PATH = "screens/MyDeliveries/components/UpcomingReturnsOrders";

const htmlTagStyles = {
  p: {
    textAlign: "center",
    color: "#000000",
    fontSize: 14,
    marginRight: 6,
  },
};

const VacationingState = ({ date }) => (
  <View style={styles.infoContainer}>
    <OnLeave />
    <Text style={styles.text1}>
      {t(`${PATH}/onLeave`, { date: getDayMonth(date) })}
    </Text>
    <Text style={styles.text2}>
      {t(`${PATH}/leaveDesc`, { date: getDayMonth(date) })}
    </Text>
  </View>
);

const UpdatingState = ({ date, pendingBannerInfo }) => (
  <View style={styles.infoContainer}>
    <Updating />
    <RenderHtml
      html={`<p>${pendingBannerInfo?.title}</p>`}
      tagsStyles={htmlTagStyles}
    />
  </View>
);

function DeliveryCard({ onPress, cardContent }) {
  return (
    <View style={styles.cardContainer}>
      <TouchableNativeFeedback
        testID="uro-delivery-card-btn"
        background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
        onPress={onPress}
      >
        <View style={styles.card}>
          <View style={styles.headerContainer}>
            <Text style={styles.headerText}>{t(`${PATH}/delivery`)}</Text>
          </View>
          <View style={styles.detailsOuterCard}>
            {cardContent?.map((content, i) => (
              <View
                key={content.text}
                style={[
                  styles.detailsCard,
                  { flex: cardContent.length === 1 ? 0 : 1 },
                ]}
              >
                <View style={styles.detailsInnercard}>
                  <View>{content.icon}</View>
                  <View style={styles.contentContainer}>
                    <Text style={styles.cardText}>{content.text}</Text>
                    <Text>
                      {content.symbol}
                      <Text style={styles.cardCount}>{content.value}</Text>
                    </Text>
                  </View>
                </View>
                {cardContent.length - 1 !== i && (
                  <View style={styles.separator} />
                )}
              </View>
            ))}
          </View>
          <View style={styles.buttonStyle}>
            <Text style={styles.viewDetailsText}>
              {t(`${PATH}/viewDetails`)}
            </Text>
            <RightArrow
              stroke={colors.extraLightBlack}
              fill={colors.extraLightBlack}
            />
          </View>
        </View>
      </TouchableNativeFeedback>
    </View>
  );
}

const ReturnCard = ({ onPress, returnCardContent }) => (
  <View style={styles.cardContainer}>
    <TouchableNativeFeedback
      testID="uro-return-card-btn"
      background={TouchableNativeFeedback.Ripple("rgba(0, 0, 0, 0.05)")}
      onPress={onPress}
    >
      <View style={styles.card}>
        <View style={styles.headerContainer}>
          <Text style={styles.headerText}>{t(`${PATH}/return`)}</Text>
        </View>
        <View style={styles.detailsOuterCard}>
          {returnCardContent.map((content, i) => (
            <View
              key={content.text}
              style={[
                styles.detailsCard,
                { flex: returnCardContent.length === 1 ? 0 : 1 },
              ]}
            >
              <View key={content.text} style={styles.detailsCard}>
                <View style={styles.detailsInnercard} key={content.text}>
                  <View>{content.icon}</View>
                  <View style={styles.contentContainer}>
                    <Text style={styles.cardText}>{content.text}</Text>
                    {content.value !== null && (
                      <Text>
                        {content.symbol}
                        <Text style={styles.cardCount}>{content.value}</Text>
                      </Text>
                    )}
                  </View>
                </View>
              </View>
              {returnCardContent.length - 1 !== i && (
                <View style={styles.separator} />
              )}
            </View>
          ))}
        </View>
        <View style={styles.buttonStyle}>
          <Text style={styles.viewDetailsText}>{t(`${PATH}/viewDetails`)}</Text>
          <RightArrow
            stroke={colors.extraLightBlack}
            fill={colors.extraLightBlack}
          />
        </View>
      </View>
    </TouchableNativeFeedback>
  </View>
);

const UpcomingReturnsOrders = ({ navigation }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deliveriesData, setDeliveriesData] = useState([]);

  const { features = {} } = useSelector(({ configReducer }) => configReducer);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getUpcomingDeliveries();
      setDeliveriesData(res?.data);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const DeliveryReturnCard = memo(({ data }) => {
    const { deliveryDetails, returnDetails } = data;

    const deliveryCardContent = [
      {
        field: "orders",
        icon: <Order />,
        text: t(`${PATH}/orders`),
        value: deliveryDetails?.noOfUsers,
      },
      ...(features?.upcoming_delivery_amount
        ? [
            {
              field: "cash",
              icon: <Cash />,
              text: t(`${PATH}/cashPay`),
              value: deliveryDetails?.totalPayableAmount?.toFixed(2),
              symbol: <Text style={styles.symbol}>{t(`${PATH}/rs`)}</Text>,
            },
          ]
        : []),
    ];

    const returnCardContent = [
      {
        field: "orders",
        icon: <ReturnOrder />,
        text: t(`${PATH}/returnProducts`),
        value: returnDetails?.noOfProducts,
      },
      ...(features.upcoming_return_amount
        ? [
            {
              field: "packets",
              icon: <Gullak />,
              text:
                returnDetails?.amountToBeReturnedToGullak === 0
                  ? t(`${PATH}/willGoToCustomer`)
                  : t(`${PATH}/willComeToGullak`),
              value: getReturnAmount(returnDetails?.amount).amount,
              symbol: <Text style={styles.symbol}>{t(`${PATH}/rs`)}</Text>,
            },
          ]
        : []),
    ];

    const onViewDeliveryPress = () => {
      analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
        screen: "Delivery Page",
        tab: "Upcoming",
        section: "Deliveries",
      });
      navigation.navigate("DeliveryOrdersList", {
        date: data?.date,
        tab: "Upcoming",
      });
    };
    const onViewReturnPress = () => {
      analytics.trackEvent(MY_DELIVERY_EVENTS.VIEW_DETAILS_CLICKED, {
        screen: "Delivery Page",
        tab: "Upcoming",
        section: "Return",
      });
      navigation.navigate("Returns", {
        date: data?.date,
        tab: "Upcoming",
      });
    };

    return (
      <View>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>
            {getDayMonth(data?.date)}
          </Text>
        </View>
        {data?.state === "Vacationing" ? (
          <VacationingState date={data?.date} />
        ) : data?.state === "Updating" ? (
          <UpdatingState
            date={data?.date}
            pendingBannerInfo={data?.pending_banner_info || {}}
          />
        ) : (
          <View>
            {deliveryDetails && (
              <DeliveryCard
                onPress={() => onViewDeliveryPress()}
                cardContent={deliveryCardContent}
              />
            )}
            {returnDetails && (
              <ReturnCard
                onPress={() => onViewReturnPress()}
                returnCardContent={returnCardContent}
              />
            )}
          </View>
        )}
      </View>
    );
  });

  const renderItem = useCallback(
    ({ item }) => <DeliveryReturnCard data={item} updatesType="upcoming" />,
    [],
  );

  const keyExtractor = (item, index) => `${item?.date}-${index}`;

  if (isLoading) {
    return (
      <View style={styles.loadingView}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }
  return (
    <View style={styles.container}>
      <FlatList
        contentContainerStyle={styles.listContainer}
        data={deliveriesData}
        renderItem={renderItem}
        ListEmptyComponent={ListEmptyComponent}
        keyExtractor={keyExtractor}
        ItemSeparatorComponent={Separator}
        onRefresh={onRefresh}
        refreshing={isRefreshing}
      />
    </View>
  );
};

function Separator() {
  return <View style={styles.cardSeparator} />;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  cardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 8,
  },
  listContainer: {
    flexGrow: 1,
    paddingBottom: 16,
    // marginTop: -8,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  card: {
    backgroundColor: colors.white,
    borderWidth: 0.5,
    borderRadius: 10,
    elevation: 2,
    borderColor: colors.lighterBlack,
  },
  headerContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  headerText: {
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
    lineHeight: 18,
    color: colors.lightBlack,
    marginHorizontal: 4,
  },
  detailsOuterCard: {
    flexDirection: "row",
    paddingVertical: 16,
    paddingLeft: 16,
    // backgroundColor: "red",
  },
  detailsInnercard: {
    flexDirection: "row",
    justifyContent: "center",
  },
  cardText: {
    fontSize: scale(14),
    lineHeight: scale(19),
    color: colors.lightBlack,
    fontFamily: "NotoSansDevanagari-Regular",
  },
  cardCount: {
    marginTop: 3,
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "NotoSansDevanagari-SemiBold",
  },
  buttonStyle: {
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderTopWidth: 0.8,
    borderColor: colors.lighterBlack,
    width: "100%",
  },
  symbol: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Regular",
    lineHeight: 26,
  },
  detailsCard: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },
  separator: {
    width: 1,
    height: 24,
    backgroundColor: colors.lighterBlack,
    marginHorizontal: 2,
  },
  viewDetailsText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    lineHeight: 20,
  },
  sectionHeader: {
    paddingTop: 16,
    paddingHorizontal: 16,
    alignSelf: "flex-start",
  },
  sectionHeaderText: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  cardSeparator: {
    height: 8,
    backgroundColor: colors.lineColor,
    marginTop: 16,
  },
  contentContainer: {
    marginHorizontal: 6,
  },
  text1: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: 18,
    fontWeight: "600",
    lineHeight: 27,
    textAlign: "center",
    color: colors.mediumBlack,
    marginVertical: 16,
  },
  text2: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 20,
    textAlign: "center",
    color: colors.lightBlack,
    maxWidth: "80%",
  },
  infoContainer: {
    alignItems: "center",
    backgroundColor: colors.white,
    marginHorizontal: 16,
    paddingHorizontal: 16,
    marginVertical: 8,
    borderWidth: 0.5,
    borderRadius: 10,
    elevation: 2,
    borderColor: colors.lighterBlack,
    height: 250,
    justifyContent: "center",
  },
});

export default UpcomingReturnsOrders;
